import type { TOC } from '@ember/component/template-only';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import {
  PrismBanner,
  PrismIcon,
  PrismLoadingBar,
  PrismModal,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { local } from 'embroider-css-modules';
import GeneralFormFieldsCancelAndConfirm from 'lumen/components/general/form-fields/cancel-and-confirm';
import type MarketParityParityManagerIssueuploadController from 'lumen/controllers/market/parity/parity-manager/issueupload';

import styles from './issueupload.module.css';

interface MarketParityParityManagerIssueuploadSignature {
  Args: {
    controller: MarketParityParityManagerIssueuploadController;
    model: unknown;
  };
}

<template>
  <PrismModal
    @onClose={{@controller.redirectOnClose}}
    @title={{t "parity_manager.issue_upload_modal.title"}}
  >
    <div>
      {{#if @controller.model.isLoading}}
        <div class="modal--medium h-1000">
          <div class="relative">
            <PrismLoadingBar />
          </div>
        </div>
      {{else}}
        <div class="modal--medium modal--padded">

          <PrismBanner
            @bannerMessage={{t "parity_manager.issue.upload.info.message"}}
            @bannerTitle={{t "parity_manager.issue.upload.info.title"}}
          />

          <form
            class="mt-200"
            enctype="multipart/form-data"
            id="issue-upload-form"
            method="post"
            name="issue-upload-form"
          >
            <div class={{styles.form-row-container}}>
              <div class={{styles.file-upload-description}}>
                {{t "parity_manager.issue.upload.screenshots_url"}}:
              </div>
              <div class={{styles.file-upload-action-container}}>
                <div class={{styles.file-upload-action-link}}>
                  {{#if @controller.model.screenshotsUrl}}
                    <a
                      class="prism-link prism-link--medium"
                      href={{@controller.model.screenshotsUrl}}
                      rel="noopener noreferrer"
                      target="_blank"
                    >{{t "parity_manager.issue.upload.view_screenshot"}}</a>
                  {{else}}
                    {{t "general.not_available_short"}}
                  {{/if}}
                </div>
              </div>
            </div>

            <div class={{styles.form-row-container}}>
              <div class={{styles.file-upload-description}}>
                {{t "parity_manager.issue.upload.extranet_file"}}:
              </div>
              <div class={{styles.file-upload-action-container}}>
                {{#if @controller.model.extranetScreenshot}}
                  <div class="truncate {{styles.file-upload-action-link}}">
                    {{@controller.model.extranetScreenshotFilename}}
                  </div>
                  <div class={{styles.file-upload-action-button-container}}>
                    <button
                      class="button--unstyled
                        {{styles.file-upload-action-button}}"
                      type="button"
                      {{on
                        "click"
                        (fn
                          @controller.downloadIssueFile
                          @controller.model.extranetScreenshot
                        )
                      }}
                    >
                      <PrismIcon
                        @color="default-inverse"
                        @name="download"
                        @size="small"
                      />
                    </button>
                    <button
                      class="button--unstyled
                        {{local
                          styles
                          'file-upload-action-button'
                          'file-upload-action-button-danger'
                        }}"
                      type="button"
                      {{on
                        "click"
                        (fn @controller.deleteIssueFile "extranet_screenshot")
                      }}
                    >
                      <PrismIcon
                        @color="default-inverse"
                        @name="bin"
                        @size="small"
                      />
                    </button>
                  </div>
                {{else}}
                  <input
                    accept=".jpg,.jpeg,.png,.pdf"
                    class="issue-upload-form-file"
                    data-name="extranet_screenshot"
                    id="issue-upload-form-file-extranet"
                    name="issue-upload-form-file-extranet_screenshot"
                    type="file"
                    {{on "change" @controller.checkFileSize}}
                  />
                {{/if}}
              </div>
            </div>

            <div class={{styles.form-row-container}}>
              <div class={{styles.file-upload-description}}>
                {{t "parity_manager.issue.upload.additional_file"}}:
              </div>
              <div class={{styles.file-upload-action-container}}>
                {{#if @controller.model.additionalScreenshot}}
                  <div class="truncate {{styles.file-upload-action-link}}">
                    {{@controller.model.additionalScreenshotFilename}}
                  </div>
                  <div class={{styles.file-upload-action-button-container}}>
                    <button
                      class="button--unstyled
                        {{styles.file-upload-action-button}}"
                      type="button"
                      {{on
                        "click"
                        (fn
                          @controller.downloadIssueFile
                          @controller.model.additionalScreenshot
                        )
                      }}
                    >
                      <PrismIcon
                        @color="default-inverse"
                        @name="download"
                        @size="small"
                      />
                    </button>
                    <button
                      class="button--unstyled
                        {{local
                          styles
                          'file-upload-action-button'
                          'file-upload-action-button-danger'
                        }}"
                      type="button"
                      {{on
                        "click"
                        (fn @controller.deleteIssueFile "additional_screenshot")
                      }}
                    >
                      <PrismIcon
                        @color="default-inverse"
                        @name="bin"
                        @size="small"
                      />
                    </button>
                  </div>
                {{else}}
                  <input
                    accept=".jpg,.jpeg,.png,.pdf"
                    class="issue-upload-form-file"
                    data-name="additional_screenshot"
                    id="issue-upload-form-file-additional"
                    type="file"
                    {{on "change" @controller.checkFileSize}}
                  />
                {{/if}}
              </div>
            </div>

            <div class={{styles.form-submit-container}}>
              <GeneralFormFieldsCancelAndConfirm
                @cancel={{@controller.redirectOnClose}}
                @confirm={{@controller.submitIssueUploadForm}}
                @confirmText={{t "general.save"}}
                @isConfirmDisabled={{@controller.isUploadDisabled}}
              />
            </div>
          </form>

        </div>
      {{/if}}
    </div>
  </PrismModal>
</template> satisfies TOC<MarketParityParityManagerIssueuploadSignature>;
